/**
 * FILL_THE_LETTERS: words with missing letters, one box per letter.
 *
 * The author brackets the missing letters right in the Text or the Question:
 * `The c[[at]] sat on the m[[a]]t.` Each `[[...]]` is a block, each letter in
 * it a box the candidate fills with exactly that letter. Whitespace inside a
 * block stays a space between boxes, it is never a box itself.
 *
 * A block is one question: it is correct when every one of its letters is.
 * Comparison ignores case unless the definition sets `caseSensitive`.
 *
 * Shared with the admin editor (sensay-assessment-admin:
 * src/utils/fillTheLetters.js). Keep the two copies in sync.
 */

import {
  BLANK_FIELDS,
  escapeHtml,
  normalizeBlankText,
  splitOnBlankMarkers,
} from "./bracketBlanks.mjs";

function toLetterBlock(content) {
  const text = normalizeBlankText(content);
  const letters = [];
  const parts = [];
  for (const char of Array.from(text)) {
    if (char === " ") {
      parts.push({ space: true });
    } else {
      parts.push({ letter: letters.length });
      letters.push(char);
    }
  }
  return letters.length ? { text, letters, parts } : null;
}

/**
 * Reads the blocks of a FILL_THE_LETTERS definition.
 *
 * @param data     the exercise data (`question`, `text`).
 * @param sanitize the platform's HTML normalization, applied before scanning
 *                 so the letters expected are the letters on screen.
 *
 * Returns `{ fields, blocks, letterCount }`: `fields.question` and
 * `fields.text` are the parts of each field (see splitOnBlankMarkers), and
 * `blocks[i]` is `{ index, field, text, letters, parts }` in document order.
 */
export function parseFillTheLetters(data, sanitize = (value) => value) {
  const blocks = [];
  const fields = {};

  BLANK_FIELDS.forEach((name) => {
    const value = data?.[name];
    if (value === null || value === undefined || value === "") {
      fields[name] = [];
      return;
    }
    fields[name] = splitOnBlankMarkers(sanitize(String(value)) ?? "", (content) => {
      const block = toLetterBlock(content);
      if (!block) return null;
      block.index = blocks.length;
      block.field = name;
      blocks.push(block);
      return block;
    });
  });

  const letterCount = blocks.reduce((sum, block) => sum + block.letters.length, 0);
  return { fields, blocks, letterCount };
}

/** One empty answer per box: `answers[block][letter]`. */
export function emptyLetterAnswers(blocks = []) {
  return (blocks || []).map((block) => block.letters.map(() => ""));
}

/**
 * The candidate's input for one box, reduced to the single letter it holds.
 * Typing into a filled box (or a mobile keyboard replacing the value) can
 * briefly leave two characters; the new one is the one that is not `previous`.
 */
export function pickBoxLetter(value, previous = "") {
  // NFC first, so a decomposed "e" + U+0301 stays one letter.
  const chars = Array.from(String(value ?? "").normalize("NFC").replace(/\s+/g, ""));
  if (chars.length <= 1) return chars[0] || "";
  const replaced = chars.indexOf(previous);
  if (previous && replaced !== -1) chars.splice(replaced, 1);
  return chars[chars.length - 1];
}

/** How many boxes are still empty. */
export function countEmptyLetterBoxes(blocks = [], answers = []) {
  let empty = 0;
  (blocks || []).forEach((block) => {
    block.letters.forEach((_, letter) => {
      if (String(answers?.[block.index]?.[letter] ?? "").trim() === "") empty++;
    });
  });
  return empty;
}

function sameLetter(expected, actual, caseSensitive) {
  const a = normalizeBlankText(expected);
  const b = normalizeBlankText(actual);
  if (!b) return false;
  return caseSensitive ? a === b : a.toLowerCase() === b.toLowerCase();
}

/**
 * The result payload for a FILL_THE_LETTERS answer. Evaluated client-side so
 * the result is final before upload, the same contract as FILL_THE_GAP: the
 * scoring service reads `selectedAnswerCorrectCount` / `questionCount`.
 *
 * `letterAnswerList[i]` is `{ answer, letters, correct, correctLetterCount }`,
 * where `answer` is the block as typed with `_` for an empty box.
 */
export function scoreFillTheLetters(blocks = [], answers = [], { caseSensitive = false } = {}) {
  let correctCount = 0;
  let letterCorrectCount = 0;
  let letterCount = 0;

  const letterAnswerList = (blocks || []).map((block) => {
    const letters = block.letters.map((_, letter) =>
      normalizeBlankText(answers?.[block.index]?.[letter]),
    );
    const correctLetterCount = block.letters.filter((expected, letter) =>
      sameLetter(expected, letters[letter], caseSensitive),
    ).length;
    const correct = correctLetterCount === block.letters.length;
    const answer = block.parts
      .map((part) => (part.space ? " " : letters[part.letter] || "_"))
      .join("");

    if (correct) correctCount++;
    letterCorrectCount += correctLetterCount;
    letterCount += block.letters.length;
    return { answer, letters, correct, correctLetterCount };
  });

  return {
    letterAnswerList,
    selectedAnswerCorrectCount: correctCount,
    questionCount: letterAnswerList.length,
    letterCorrectCount,
    letterCount,
  };
}

/**
 * Markup of one block for the candidate: an input per letter. The inputs are
 * injected via {@html}, so the component reads them back through the data
 * attributes. `label(blockNumber, letterNumber, letterTotal)` names each box
 * for screen readers.
 */
export function renderLetterBlock(block, label = () => "") {
  const boxes = block.parts
    .map((part) => {
      if (part.space) return `<span class="fl-space" aria-hidden="true"></span>`;
      const name = escapeHtml(label(block.index + 1, part.letter + 1, block.letters.length));
      return (
        `<input type="text" class="fl-box" data-fl-block="${block.index}" ` +
        `data-fl-letter="${part.letter}" aria-label="${name}" value="" ` +
        `autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" />`
      );
    })
    .join("");
  return `<span class="fl-block" data-fl-block="${block.index}">${boxes}</span>`;
}
